import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, ListChecks, Languages, LogOut, ArrowLeftRight, Moon, Sun, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { useAuthStore } from '@/stores/authStore';
import { logoutRequest } from '@/api/auth';
import { useTheme } from '@/hooks/useTheme';

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const { theme, cycle: cycleTheme } = useTheme();

  async function handleLogout() {
    try {
      await logoutRequest();
    } catch {
      // ignore — clear local state anyway
    }
    clear();
    toast.success(t('auth.loggedOut'));
    navigate('/login', { replace: true });
  }

  function toggleLocale() {
    const next = i18n.language?.startsWith('tr') ? 'en' : 'tr';
    void i18n.changeLanguage(next);
  }

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const themeLabel = t(`theme.${theme}`);

  const navItems = [
    { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard, end: true },
    { to: '/records', label: t('nav.records'), icon: ListChecks },
    { to: '/import-export', label: t('nav.importExport'), icon: ArrowLeftRight },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 dark:bg-slate-950">
      <aside className="md:w-60 md:min-h-screen border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="px-5 py-5 border-b border-slate-200 dark:border-slate-800">
          <h1 className="text-lg font-bold text-brand-700 dark:text-brand-300">
            {t('app.name')}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">{t('app.tagline')}</p>
        </div>
        <nav className="p-3 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition',
                  isActive
                    ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-medium'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                ].join(' ')
              }
            >
              <item.icon className="h-4 w-4" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between px-4 md:px-8 py-3 border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur sticky top-0 z-10">
          <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
            {user?.displayName}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cycleTheme} className="btn-secondary text-xs" aria-label={t('theme.toggle')} title={`${t('theme.toggle')} (${themeLabel})`}>
              <ThemeIcon className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">{themeLabel}</span>
            </button>
            <button onClick={toggleLocale} className="btn-secondary text-xs" aria-label="Toggle language">
              <Languages className="h-4 w-4" />
              {i18n.language?.toUpperCase() ?? 'TR'}
            </button>
            <button onClick={handleLogout} className="btn-secondary">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
